import React from 'react';
import { Typography, Link } from '@mui/material';

/**
 * Component that automatically detects URLs in text and renders them as clickable links
 * @param {string} text - The text to linkify
 * @param {object} typographyProps - Props to pass to the Typography component
 * @param {object} linkProps - Props to pass to the Link components
 */
const LinkifiedText = ({ text, typographyProps = {}, linkProps = {} }) => {
  if (!text) return null;

  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}[^\s]*)/gi;

  // Split text into parts and identify URLs
  const parts = [];
  let lastIndex = 0;
  let match;

  const textStr = String(text);
  const regex = new RegExp(urlRegex);

  while ((match = regex.exec(textStr)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: textStr.substring(lastIndex, match.index)
      });
    }

    // Add the URL
    let url = match[0];
    let href = url;
    
    // Add protocol if missing
    if (!href.startsWith('http://') && !href.startsWith('https://')) {
      href = 'https://' + href;
    }

    parts.push({
      type: 'link',
      content: url,
      href: href
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last URL
  if (lastIndex < textStr.length) {
    parts.push({
      type: 'text',
      content: textStr.substring(lastIndex)
    });
  }

  // If no URLs found, just return the text
  if (parts.length === 0) {
    return <Typography {...typographyProps}>{text}</Typography>;
  }

  // Render parts with links
  return (
    <Typography {...typographyProps}>
      {parts.map((part, index) => {
        if (part.type === 'link') {
          return (
            <Link
              key={index}
              href={part.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'primary.main',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none'
                }
              }}
              {...linkProps}
            >
              {part.content}
            </Link>
          );
        }
        return <React.Fragment key={index}>{part.content}</React.Fragment>;
      })}
    </Typography>
  );
};

export default LinkifiedText;